(function () {
  const GUARD_ATTR = 'data-naturrum-dpo-guard';
  const INVALID_ATTR = 'data-naturrum-dpo-invalid';
  const TOUCHED_ATTR = 'data-naturrum-dpo-touched';
  const HELP_CLASS = 'naturrum-dpo-field-help';
  const ERROR_CLASS = 'naturrum-dpo-field-error';
  const BLOCKED_CLASS = 'naturrum-dpo-guard-blocked';
  const PAYMENT_HIDDEN_CLASS = 'naturrum-dpo-payment-hidden';
  const PRICE_SUPPRESSED_CLASS = 'naturrum-dpo-price-suppressed';
  const PRICE_ORIGINAL_HIDDEN_ATTR = 'data-naturrum-dpo-original-hidden';
  const PRICE_CALCULATED_ATTR = 'data-naturrum-dpo-price-calculated';
  const PRICE_CONTROLS_ATTR = 'data-naturrum-dpo-price-controls';
  const PRICE_BUTTON_ATTR = 'data-naturrum-dpo-calculate-price';
  const PRICE_MESSAGE_ATTR = 'data-naturrum-dpo-price-message';
  const VISIBLE_PRICE_ATTR = 'data-naturrum-dpo-visible-price';
  const TRACKING_SELECTOR = '[data-naturrum-dpo-tracking]';
  const ANALYTICS_STARTED_ATTR = 'data-naturrum-dpo-analytics-started';
  const LAST_CALCULATED_FINGERPRINT_ATTR = 'data-naturrum-dpo-last-calculated-fingerprint';
  const LAST_PRICE_FINGERPRINT_ATTR = 'data-naturrum-dpo-last-price-fingerprint';
  const LAST_PRICE_EVENT_ID_ATTR = 'data-naturrum-dpo-last-price-event-id';
  const LAST_CHANGE_FINGERPRINT_ATTR = 'data-naturrum-dpo-last-change-fingerprint';
  const PROPORTION_NOTICE_ATTR = 'data-naturrum-dpo-proportion-notice';
  const PROPORTION_NOTICE_SOURCE_SELECTOR = '[data-naturrum-dpo-proportion-notice-source]';
  const PROPERTY_FORM_NAME_PATTERN = /^properties\[(.+)\]$/u;
  const PRODUCTION_MEASURE_PROPERTY = 'Produktionsmål';
  const CONFIGURATION_ID_PROPERTY = '_naturrum konfigurations-id';
  const PRICE_SHOWN_EVENT_ID_PROPERTY = '_naturrum prisvisning-event-id';
  const PRICE_GROUP_PROPERTY = '_naturrum prisgruppe';
  const PRICE_INTERVAL_PROPERTY = '_naturrum prisinterval';
  const PRICE_AGGREGATE_EVENT_NAME = 'naturrum_price_calculated_aggregate';
  const DPO_INTERNAL_PROPERTIES_TO_DROP = new Set(['__dpo_hash', '__dpo_p']);
  const CHANGE_TRACKING_DEBOUNCE_MS = 700;

  function normalizeLabel(label) {
    return String(label || '')
      .toLowerCase()
      .replace(/æ/g, 'ae')
      .replace(/ø/g, 'oe')
      .replace(/å/g, 'aa');
  }

  function dimensionKeyFromLabel(label) {
    const normalized = normalizeLabel(label);
    if (normalized.includes('diameter')) return 'diameter';
    if (normalized.includes('laengde')) return 'length';
    if (normalized.includes('bredde') || normalized.includes('dybde')) return 'width';
    if (normalized.includes('hoejde')) return 'height';
    return '';
  }

  function propertyNameFromFormName(name) {
    const match = String(name || '').match(PROPERTY_FORM_NAME_PATTERN);
    return match?.[1] || '';
  }

  function productionGoalTextFromDimensions(dimensions) {
    const length = String(dimensions?.length || '').trim();
    const width = String(dimensions?.width || '').trim();
    const height = String(dimensions?.height || '').trim();
    const diameter = String(dimensions?.diameter || '').trim();

    if (diameter && height) return `Ø${diameter} x ${height} cm`;
    if (length && width && height) return `${length} x ${width} x ${height} cm`;
    return '';
  }

  function dimensionValuesFromProperties(properties) {
    const dimensions = {};
    for (const [name, value] of Object.entries(properties || {})) {
      const key = dimensionKeyFromLabel(name);
      if (key && value != null && String(value).trim()) {
        dimensions[key] = String(value).trim();
      }
    }
    return dimensions;
  }

  function dimensionValuesFromRows(rows) {
    const dimensions = {};
    for (const row of rows || []) {
      if (row?.key && row.input?.value != null && String(row.input.value).trim()) {
        dimensions[row.key] = String(row.input.value).trim();
      }
    }
    return dimensions;
  }

  function parseNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function parseWholeCm(value) {
    const trimmed = String(value || '').trim();
    if (!/^\d+$/u.test(trimmed)) return null;
    return Number(trimmed);
  }

  function compactObject(value) {
    const output = {};
    for (const [key, item] of Object.entries(value || {})) {
      if (item === undefined || item === null || item === '') continue;
      output[key] = item;
    }
    return output;
  }

  function readJsonScript(documentRef, selector) {
    const node = documentRef.querySelector?.(selector);
    if (!node?.textContent) return {};

    try {
      return JSON.parse(node.textContent || '{}') || {};
    } catch {
      return {};
    }
  }

  function readTrackingConfig(root) {
    const documentRef = root.ownerDocument || document;
    return readJsonScript(documentRef, TRACKING_SELECTOR);
  }

  function analyticsApi(root) {
    return root?.ownerDocument?.defaultView?.NaturrumConfiguratorAnalytics
      || (typeof window !== 'undefined' ? window.NaturrumConfiguratorAnalytics : null)
      || null;
  }

  function productPayload(root) {
    const config = readTrackingConfig(root);
    return compactObject({
      product_id: parseNumber(config.product_id),
      product_handle: config.product_handle || null,
      product_title: config.product_title || null,
      product_type: config.product_type || null,
      product_vendor: config.product_vendor || null,
      product_family: config.product_family || 'specialmaal',
      section_id: config.section_id || null,
    });
  }

  function currentVariantId(root) {
    const form = root.closest?.('form');
    const formValue = form?.querySelector?.('[name="id"]')?.value;
    return parseNumber(formValue) || parseNumber(readTrackingConfig(root).variant_id);
  }

  function parseDkkPriceText(text) {
    const normalized = String(text || '').replace(/\s+/gu, '');
    const matches = [...normalized.matchAll(/(\d{1,3}(?:\.\d{3})*|\d+)(?:,(\d{2}))?/gu)];
    const prices = matches
      .map((match) => {
        const whole = match[1].replace(/\./gu, '');
        const cents = match[2] || '00';
        const number = Number(`${whole}.${cents}`);
        return Number.isFinite(number) ? number : null;
      })
      .filter((number) => number != null);

    return prices.findLast((number) => number > 0) ?? prices[prices.length - 1] ?? null;
  }

  function currentShownPriceDkk(root) {
    for (const target of productPriceTargets(root)) {
      const price = parseDkkPriceText(target.textContent);
      if (price != null) return price;
    }
    return null;
  }

  function bucketCm(value) {
    const number = parseNumber(value);
    if (number == null || number <= 0) return null;

    const start = Math.floor((number - 1) / 50) * 50 + 1;
    const end = start + 49;
    return `${start}-${end} cm`;
  }

  function dimensionBucket(shape, dimensions) {
    if (shape === 'round') {
      return [`D:${bucketCm(dimensions.diameter)}`, `H:${bucketCm(dimensions.height)}`].join('|');
    }
    return [
      `L:${bucketCm(dimensions.length)}`,
      `B:${bucketCm(dimensions.width)}`,
      `H:${bucketCm(dimensions.height)}`,
    ].join('|');
  }

  function priceGroup(shape, dimensions) {
    if (shape === 'round') return `D${dimensions.diameter}-H${dimensions.height}`;
    return `L${dimensions.length}-B${dimensions.width}-H${dimensions.height}`;
  }

  function buildDpoConfigurationPayload(root, rows, state = {}) {
    const rawDimensions = dimensionValuesFromRows(rows);
    const dimensions = Object.fromEntries(
      Object.entries(rawDimensions).map(([key, value]) => [key, parseWholeCm(value)])
    );
    const shape = dimensions.diameter ? 'round' : 'rectangular';
    const valid = state.valid === true;
    const priceCalculated = state.priceCalculated !== false;
    const tracking = readTrackingConfig(root);
    const firstInvalid = state.results?.find?.((item) => !item.result?.valid);
    const interval = productionGoalTextFromDimensions(dimensions);
    const payload = compactObject({
      shape,
      currency: tracking.currency || 'DKK',
      is_valid_configuration: valid,
      dimension_bucket: dimensionBucket(shape, dimensions),
      price_group: valid ? priceGroup(shape, dimensions) : null,
      price_interval: valid ? interval : null,
      shown_price_dkk: valid && priceCalculated ? currentShownPriceDkk(root) : null,
      variant_id: valid ? currentVariantId(root) : null,
      sku: tracking.sku || null,
      validation_error_type: valid ? null : firstInvalid?.row?.key || 'configuration',
      validation_error_message: valid ? null : firstInvalid?.result?.message || 'Konfigurationen er ikke gyldig.',
    });

    if (shape === 'round') {
      Object.assign(payload, compactObject({
        diameter_cm: dimensions.diameter,
        height_cm: dimensions.height,
        price_diameter_cm: valid ? dimensions.diameter : null,
        price_height_cm: valid ? dimensions.height : null,
      }));
      return payload;
    }

    Object.assign(payload, compactObject({
      length_cm: dimensions.length,
      width_cm: dimensions.width,
      height_cm: dimensions.height,
      price_length_cm: valid ? dimensions.length : null,
      price_width_cm: valid ? dimensions.width : null,
      price_height_cm: valid ? dimensions.height : null,
    }));
    return payload;
  }

  function configurationFingerprint(configuration) {
    return JSON.stringify(configuration || {});
  }

  function shapeDpoPropertiesObject(properties, dimensionOverrides = {}) {
    const currentProperties = properties || {};
    const dimensions = {
      ...dimensionValuesFromProperties(currentProperties),
      ...dimensionOverrides,
    };
    const productionGoal = productionGoalTextFromDimensions(dimensions);
    if (!productionGoal) return { ...currentProperties };

    const shaped = {};
    for (const [name, value] of Object.entries(currentProperties)) {
      if (name === PRODUCTION_MEASURE_PROPERTY) continue;
      if (DPO_INTERNAL_PROPERTIES_TO_DROP.has(name)) continue;
      if (dimensionKeyFromLabel(name)) continue;
      shaped[name] = value;
    }

    shaped[PRODUCTION_MEASURE_PROPERTY] = productionGoal;
    return shaped;
  }

  function collectPropertyEntriesFromFormData(formData) {
    const properties = {};
    if (!formData?.entries) return properties;

    for (const [formName, value] of formData.entries()) {
      const propertyName = propertyNameFromFormName(formName);
      if (propertyName) properties[propertyName] = value;
    }
    return properties;
  }

  function shapeDpoFormData(formData, dimensionOverrides = {}) {
    const properties = collectPropertyEntriesFromFormData(formData);
    const shaped = shapeDpoPropertiesObject(properties, dimensionOverrides);
    const productionGoal = shaped[PRODUCTION_MEASURE_PROPERTY];
    if (!productionGoal || !formData?.delete || !formData?.set) return false;

    for (const name of Object.keys(properties)) {
      formData.delete(`properties[${name}]`);
    }
    for (const [name, value] of Object.entries(shaped)) {
      formData.set(`properties[${name}]`, value);
    }
    return true;
  }

  function displayName(label) {
    const key = dimensionKeyFromLabel(label);
    const names = {
      diameter: 'diameter',
      length: 'længde',
      width: 'bredde',
      height: 'højde',
    };
    return names[key] || String(label || 'mål').replace(/\s*\(cm\)\s*/i, '').toLowerCase();
  }

  function rangeHelpText(range) {
    if (!range || range.min == null || range.max == null) return '';
    return `Min. ${range.min} cm - maks. ${range.max} cm`;
  }

  function validateDimensionValue(value, range, label) {
    const name = displayName(label);
    const trimmed = String(value || '').trim();

    if (!trimmed) {
      return { valid: false, message: `Udfyld ${name} i hele cm.` };
    }
    if (!/^\d+$/u.test(trimmed)) {
      return { valid: false, message: `Brug kun hele cm for ${name}.` };
    }

    const numberValue = Number(trimmed);
    if (range?.min != null && numberValue < Number(range.min)) {
      return { valid: false, message: `Mindste ${name} er ${range.min} cm.` };
    }
    if (range?.max != null && numberValue > Number(range.max)) {
      return { valid: false, message: `Største ${name} er ${range.max} cm.` };
    }

    return { valid: true, message: '' };
  }

  function readRules(documentRef) {
    const rulesNode = documentRef.querySelector?.('[data-naturrum-dpo-rules]');
    if (!rulesNode) return {};

    try {
      return JSON.parse(rulesNode.textContent || '{}') || {};
    } catch {
      return {};
    }
  }

  function isRendered(element) {
    if (!element || element.disabled) return false;
    const rect = element.getBoundingClientRect();
    return Boolean(rect.width && rect.height && (element.offsetWidth || element.offsetHeight || element.getClientRects().length));
  }

  function fieldLabel(field) {
    return field?.querySelector('label')?.textContent?.replace(/\s+/gu, ' ').trim() || '';
  }

  function ensureMessage(field, className, suffix) {
    let node = field.querySelector(`.${className}`);
    if (!node) {
      node = document.createElement('p');
      node.className = className;
      node.id = `${field.id || 'dpo-field'}-${suffix}`;
      field.appendChild(node);
    }
    return node;
  }

  function connectDescribedBy(input, ids) {
    const current = String(input.getAttribute('aria-describedby') || '')
      .split(/\s+/u)
      .filter(Boolean);
    input.setAttribute('aria-describedby', [...new Set([...current, ...ids])].join(' '));
  }

  function fieldsFor(container, rules) {
    return [...container.querySelectorAll('input[name^="options["]')]
      .map((input) => {
        const field = input.closest('.field');
        const label = fieldLabel(field);
        const key = dimensionKeyFromLabel(label);
        return {
          input,
          field,
          label,
          key,
          range: rules[key],
        };
      })
      .filter((row) => row.field && row.key && row.range && isRendered(row.input));
  }

  function propertyInputsFor(form) {
    return [...form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]')]
      .map((input) => ({
        input,
        propertyName: propertyNameFromFormName(input.getAttribute('name')),
      }))
      .filter((item) => item.propertyName);
  }

  function syncDpoPropertyInputs(form, dimensions) {
    if (!form) return false;

    const propertyInputs = propertyInputsFor(form);
    const currentProperties = {};
    for (const { input, propertyName } of propertyInputs) {
      currentProperties[propertyName] = input.value;
    }

    const shaped = shapeDpoPropertiesObject(currentProperties, dimensions);
    const productionGoal = shaped[PRODUCTION_MEASURE_PROPERTY];
    if (!productionGoal) return false;

    for (const { input, propertyName } of propertyInputs) {
      if (!(propertyName in shaped)) input.remove();
    }

    for (const [propertyName, value] of Object.entries(shaped)) {
      let input = propertyInputs.find((item) => item.propertyName === propertyName)?.input || null;
      if (!input || input.isConnected === false) {
        input = form.ownerDocument.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${propertyName}]`;
        form.appendChild(input);
      }
      input.value = value;
    }
    return true;
  }

  function propertyInputFor(form, propertyName) {
    return propertyInputsFor(form).find((item) => item.propertyName === propertyName)?.input || null;
  }

  function setHiddenPropertyInput(form, propertyName, value) {
    if (!form) return;

    let input = propertyInputFor(form, propertyName);
    if (value == null || value === '') {
      input?.remove?.();
      return;
    }

    if (!input || input.isConnected === false) {
      input = form.ownerDocument.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${propertyName}]`;
      form.appendChild(input);
    }
    input.value = String(value);
  }

  function clearQuoteTrackingPropertyInputs(form) {
    if (!form) return;

    [
      CONFIGURATION_ID_PROPERTY,
      PRICE_SHOWN_EVENT_ID_PROPERTY,
      PRICE_GROUP_PROPERTY,
      PRICE_INTERVAL_PROPERTY,
    ].forEach((propertyName) => setHiddenPropertyInput(form, propertyName, ''));
  }

  function configurationIdFor(root, configuration, event) {
    if (event?.configuration_id) return event.configuration_id;
    const api = analyticsApi(root);
    return api?.configurationId?.(configuration) || null;
  }

  function syncQuoteTrackingPropertyInputs(form, root, configuration, event) {
    const configurationId = configurationIdFor(root, configuration, event);
    const priceShownEventId = event?.event_id || root.getAttribute(LAST_PRICE_EVENT_ID_ATTR) || '';

    setHiddenPropertyInput(form, CONFIGURATION_ID_PROPERTY, configurationId);
    setHiddenPropertyInput(form, PRICE_SHOWN_EVENT_ID_PROPERTY, priceShownEventId);
    setHiddenPropertyInput(form, PRICE_GROUP_PROPERTY, configuration.price_group || configuration.dimension_bucket || '');
    setHiddenPropertyInput(form, PRICE_INTERVAL_PROPERTY, configuration.price_interval || productionGoalTextFromDimensions(configuration));
    return configurationId;
  }

  function trackConfiguratorEvent(root, eventName, configuration, context = {}) {
    const api = analyticsApi(root);
    if (!api?.track) return null;

    return api.track(eventName, {
      product: productPayload(root),
      configuration,
      context,
    });
  }

  function trackPriceAggregate(root, configuration, context = {}) {
    const api = analyticsApi(root);
    if (!api?.trackPriceAggregate) return null;

    return api.trackPriceAggregate({
      product: productPayload(root),
      configuration,
      context: {
        ...context,
        event_name: PRICE_AGGREGATE_EVENT_NAME,
      },
    });
  }

  function trackStartedIfNeeded(root, configuration, context) {
    if (root.getAttribute(ANALYTICS_STARTED_ATTR) === 'true') return null;

    const event = trackConfiguratorEvent(root, 'naturrum_configurator_started', configuration, context);
    if (event) root.setAttribute(ANALYTICS_STARTED_ATTR, 'true');
    return event;
  }

  function trackCalculatedPriceIfNeeded(root, configuration, context) {
    if (!configuration?.is_valid_configuration) return null;

    const fingerprint = configurationFingerprint(configuration);
    if (root.getAttribute(LAST_CALCULATED_FINGERPRINT_ATTR) === fingerprint) return null;

    const event = trackConfiguratorEvent(root, 'naturrum_price_calculated', configuration, context);
    root.setAttribute(LAST_CALCULATED_FINGERPRINT_ATTR, fingerprint);
    if (event?.event_id) root.setAttribute(LAST_PRICE_EVENT_ID_ATTR, event.event_id);
    return event;
  }

  function trackValidPriceIfNeeded(root, configuration, context) {
    if (!configuration?.is_valid_configuration) return null;

    const fingerprint = configurationFingerprint(configuration);
    if (root.getAttribute(LAST_PRICE_FINGERPRINT_ATTR) === fingerprint) return null;

    const event = trackConfiguratorEvent(root, 'naturrum_valid_price_shown', configuration, context);
    root.setAttribute(LAST_PRICE_FINGERPRINT_ATTR, fingerprint);
    if (event?.event_id) root.setAttribute(LAST_PRICE_EVENT_ID_ATTR, event.event_id);
    return event;
  }

  function scheduleChangedTracking(root, configuration, context) {
    const fingerprint = configurationFingerprint(configuration);
    if (root.getAttribute(LAST_CHANGE_FINGERPRINT_ATTR) === fingerprint) return;

    clearTimeout(root._naturrumDpoSpecialmaalChangeTimer);
    root._naturrumDpoSpecialmaalChangeTimer = setTimeout(() => {
      const event = trackConfiguratorEvent(root, 'naturrum_configurator_changed', configuration, context);
      if (event) root.setAttribute(LAST_CHANGE_FINGERPRINT_ATTR, fingerprint);
    }, CHANGE_TRACKING_DEBOUNCE_MS);
  }

  function decorateField(row) {
    const { input, field, range } = row;
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('pattern', '[0-9]*');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('required', 'required');
    input.setAttribute('data-naturrum-dpo-dimension', row.key);
    input.setAttribute('data-naturrum-dpo-min', String(range.min));
    input.setAttribute('data-naturrum-dpo-max', String(range.max));

    const help = ensureMessage(field, HELP_CLASS, 'help');
    const error = ensureMessage(field, ERROR_CLASS, 'error');
    help.textContent = rangeHelpText(range);
    error.textContent = '';
    error.hidden = true;
    connectDescribedBy(input, [help.id, error.id]);
  }

  function setFieldState(row, result, showError) {
    const { input, field } = row;
    const error = field.querySelector(`.${ERROR_CLASS}`);
    const shouldShow = showError || input.hasAttribute(TOUCHED_ATTR);

    if (result.valid || !shouldShow) {
      input.removeAttribute(INVALID_ATTR);
      input.removeAttribute('aria-invalid');
      field.removeAttribute(INVALID_ATTR);
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
      return;
    }

    input.setAttribute(INVALID_ATTR, 'true');
    input.setAttribute('aria-invalid', 'true');
    field.setAttribute(INVALID_ATTR, 'true');
    if (error) {
      error.textContent = result.message;
      error.hidden = false;
    }
  }

  function validationState(rows, showErrors) {
    const results = rows.map((row) => {
      const result = validateDimensionValue(row.input.value, row.range, row.label);
      setFieldState(row, result, showErrors);
      return { row, result };
    });

    return {
      valid: results.every((item) => item.result.valid),
      firstInvalid: results.find((item) => !item.result.valid)?.row?.input || null,
      results,
      suppressPrice: shouldSuppressPriceForResults(results, showErrors),
    };
  }

  function shouldSuppressPriceForResults(results, showErrors) {
    return results.some((item) => !item.result.valid
      && (showErrors || item.row.input.hasAttribute(TOUCHED_ATTR)));
  }

  function addButtons(root) {
    const form = root.closest('form');
    const scope = form?.closest('product-form-component') || form || root;
    return [...scope.querySelectorAll('button')]
      .filter((button) => /læg i indkøbskurven|add to cart/i.test(button.textContent || '')
        || /add-to-cart|ProductSubmitButton|itoris_dynamicoptions_add_to_cart/i.test(button.id || button.className || ''));
  }

  function paymentButtons(root) {
    const form = root.closest('form');
    const scope = form?.closest('product-form-component') || form || root;
    return [...scope.querySelectorAll('.shopify-payment-button, shopify-payment-terms, [data-shopify="payment-button"]')];
  }

  function productPriceTargets(root) {
    const details = root.closest('[data-testid="product-information-details"]');
    if (!details) return [];
    return [...details.querySelectorAll('product-price[data-product-id]')];
  }

  function setPriceSuppressedState(root, suppressed) {
    for (const target of productPriceTargets(root)) {
      if (!target.hasAttribute(PRICE_ORIGINAL_HIDDEN_ATTR)) {
        target.setAttribute(PRICE_ORIGINAL_HIDDEN_ATTR, target.hidden ? 'true' : 'false');
      }

      const originallyHidden = target.getAttribute(PRICE_ORIGINAL_HIDDEN_ATTR) === 'true';
      target.classList.toggle(PRICE_SUPPRESSED_CLASS, suppressed);

      if (suppressed) {
        target.hidden = true;
        target.setAttribute('aria-hidden', 'true');
      } else if (!originallyHidden) {
        target.hidden = false;
        target.removeAttribute('aria-hidden');
      }
    }
  }

  function priceCalculationControls(root) {
    const documentRef = root.ownerDocument || document;
    return documentRef.querySelector?.(`[${PRICE_CONTROLS_ATTR}]`) || null;
  }

  function priceCalculationButton(root) {
    return priceCalculationControls(root)?.querySelector?.(`[${PRICE_BUTTON_ATTR}]`) || null;
  }

  function setPriceCalculationMessage(root, message) {
    const messageNode = priceCalculationControls(root)?.querySelector?.(`[${PRICE_MESSAGE_ATTR}]`);
    if (!messageNode) return;
    messageNode.textContent = message || '';
    messageNode.hidden = !message;
  }

  function visiblePriceNode(root) {
    return priceCalculationControls(root)?.querySelector?.(`[${VISIBLE_PRICE_ATTR}]`) || null;
  }

  function formatDkkPrice(price) {
    if (!Number.isFinite(price)) return '';
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }

  function showCalculatedPrice(root, price) {
    const node = visiblePriceNode(root);
    if (!node) return;

    const text = formatDkkPrice(price);
    node.textContent = text;
    node.hidden = !text;
  }

  function hideCalculatedPrice(root) {
    const node = visiblePriceNode(root);
    if (!node) return;

    node.textContent = '';
    node.hidden = true;
  }

  function isPriceCalculated(root) {
    return root.getAttribute(PRICE_CALCULATED_ATTR) === 'true';
  }

  function setPriceCalculated(root, calculated, message = '') {
    root.setAttribute(PRICE_CALCULATED_ATTR, calculated ? 'true' : 'false');
    setPriceCalculationMessage(root, message);

    if (!calculated) {
      root.removeAttribute(LAST_CALCULATED_FINGERPRINT_ATTR);
      root.removeAttribute(LAST_PRICE_FINGERPRINT_ATTR);
      root.removeAttribute(LAST_PRICE_EVENT_ID_ATTR);
      root._naturrumDpoSpecialmaalCalculatedConfiguration = null;
      hideCalculatedPrice(root);
      clearQuoteTrackingPropertyInputs(root.closest?.('form'));
    }
  }

  function setBlockedState(root, blocked, suppressPrice = blocked) {
    root.classList.toggle(BLOCKED_CLASS, blocked);
    setPriceSuppressedState(root, suppressPrice);

    for (const button of addButtons(root)) {
      if (!button.hasAttribute('data-naturrum-dpo-original-disabled')) {
        button.setAttribute('data-naturrum-dpo-original-disabled', button.disabled ? 'true' : 'false');
      }
      if (blocked) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.getAttribute('data-naturrum-dpo-original-disabled') !== 'true') {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    }

    for (const payment of paymentButtons(root)) {
      payment.classList.toggle(PAYMENT_HIDDEN_CLASS, blocked);
      payment.toggleAttribute('hidden', blocked);
      payment.setAttribute('aria-hidden', blocked ? 'true' : 'false');
    }
  }

  function ensureStyle(documentRef) {
    if (documentRef.getElementById('NaturrumDpoSpecialmaalGuardStyle')) return;
    const style = documentRef.createElement('style');
    style.id = 'NaturrumDpoSpecialmaalGuardStyle';
    style.textContent = `
      #itoris_dynamicproductoptions .${HELP_CLASS} {
        color: rgb(var(--color-foreground-rgb) / 0.62);
        font-size: 0.82rem;
        line-height: 1.35;
        margin: 0.35rem 0 0;
      }
      #itoris_dynamicproductoptions .${ERROR_CLASS} {
        color: #9f2617;
        font-size: 0.82rem;
        line-height: 1.35;
        margin: 0.35rem 0 0;
      }
      #itoris_dynamicproductoptions .field[${INVALID_ATTR}='true'] input,
      #itoris_dynamicproductoptions input[${INVALID_ATTR}='true'] {
        border-color: #9f2617 !important;
        color: #9f2617 !important;
        box-shadow: 0 0 0 1px #9f2617 inset;
      }
      .${PAYMENT_HIDDEN_CLASS} {
        display: none !important;
      }
      .${PRICE_SUPPRESSED_CLASS} {
        display: none !important;
      }
      .naturrum-dpo-price-controls {
        display: grid;
        gap: 0.55rem;
        margin: 1rem 0 0;
      }
      .naturrum-dpo-price-controls .button {
        width: 100%;
      }
      .naturrum-dpo-price-message {
        color: rgb(var(--color-foreground-rgb) / 0.68);
        font-size: 0.86rem;
        line-height: 1.35;
        margin: 0;
      }
      .naturrum-dpo-visible-price {
        font-size: clamp(1.25rem, 1.9vw, 1.65rem);
        font-weight: 650;
        line-height: 1.15;
      }
    `;
    documentRef.head.appendChild(style);
  }

  function placeImageProportionNotice(documentRef, container) {
    if (!container?.insertAdjacentElement) return;

    const existing = documentRef.querySelector?.(`[${PROPORTION_NOTICE_ATTR}]`);
    if (existing) {
      if (existing.previousElementSibling !== container) container.insertAdjacentElement('afterend', existing);
      return;
    }

    const source = documentRef.querySelector?.(PROPORTION_NOTICE_SOURCE_SELECTOR);
    const notice = source?.querySelector?.('.product-image-proportion-notice');
    if (!notice) return;

    notice.setAttribute(PROPORTION_NOTICE_ATTR, 'true');
    container.insertAdjacentElement('afterend', notice);
  }

  function ensurePriceCalculationControls(documentRef, container, rules) {
    const existing = documentRef.querySelector?.(`[${PRICE_CONTROLS_ATTR}]`);
    if (existing) return existing;

    const controls = documentRef.createElement('div');
    controls.className = 'naturrum-dpo-price-controls';
    controls.setAttribute(PRICE_CONTROLS_ATTR, 'true');

    const button = documentRef.createElement('button');
    button.type = 'button';
    button.className = 'button button-secondary';
    button.textContent = 'Beregn pris';
    button.setAttribute(PRICE_BUTTON_ATTR, 'true');

    const message = documentRef.createElement('p');
    message.className = 'naturrum-dpo-price-message';
    message.setAttribute(PRICE_MESSAGE_ATTR, 'true');
    message.setAttribute('aria-live', 'polite');
    message.hidden = true;

    const visiblePrice = documentRef.createElement('div');
    visiblePrice.className = 'naturrum-dpo-visible-price';
    visiblePrice.setAttribute(VISIBLE_PRICE_ATTR, 'true');
    visiblePrice.setAttribute('aria-live', 'polite');
    visiblePrice.hidden = true;

    button.addEventListener('click', () => {
      const currentRows = fieldsFor(container, rules);
      for (const row of currentRows) row.input.setAttribute(TOUCHED_ATTR, 'true');

      const state = validationState(currentRows, true);
      if (!state.valid) {
        setPriceCalculated(container, false, 'Ret felterne før du beregner prisen.');
        setBlockedState(container, true, true);
        state.firstInvalid?.focus();
        return;
      }

      const form = container.closest('form');
      if (form) syncDpoPropertyInputs(form, dimensionValuesFromRows(currentRows));

      setPriceCalculated(container, true, 'Prisen er beregnet for de viste mål.');
      setBlockedState(container, false, true);

      const configuration = buildDpoConfigurationPayload(container, currentRows, state);
      const context = {
        source: 'dpo_specialmaal_guard',
        trigger: 'calculate_price',
      };
      showCalculatedPrice(container, configuration.shown_price_dkk);
      trackPriceAggregate(container, configuration, context);
      trackStartedIfNeeded(container, configuration, context);
      const priceEvent = trackCalculatedPriceIfNeeded(container, configuration, context);
      const validPriceEvent = trackValidPriceIfNeeded(container, configuration, context);
      syncQuoteTrackingPropertyInputs(form, container, configuration, validPriceEvent || priceEvent);
      container._naturrumDpoSpecialmaalCalculatedConfiguration = configuration;
    });

    controls.append(button, visiblePrice, message);

    const notice = documentRef.querySelector?.(`[${PROPORTION_NOTICE_ATTR}]`);
    const anchor = notice || container;
    anchor.insertAdjacentElement('afterend', controls);

    return controls;
  }

  function applyGuard(documentRef) {
    const container = documentRef.querySelector?.('#itoris_dynamicproductoptions');
    if (!container) return false;

    const rules = readRules(documentRef);
    const rows = fieldsFor(container, rules);
    if (rows.length === 0) return false;

    ensureStyle(documentRef);
    placeImageProportionNotice(documentRef, container);
    ensurePriceCalculationControls(documentRef, container, rules);
    if (!container.hasAttribute(PRICE_CALCULATED_ATTR)) {
      setPriceCalculated(container, false, 'Beregn pris for at se prisen.');
    }

    for (const row of rows) {
      decorateField(row);
      if (!row.input.hasAttribute(GUARD_ATTR)) {
        row.input.setAttribute(GUARD_ATTR, 'true');
        row.input.addEventListener('input', () => {
          row.input.setAttribute(TOUCHED_ATTR, 'true');
          setPriceCalculated(container, false, 'Ret mål og beregn prisen igen.');
          const currentRows = fieldsFor(container, rules);
          const state = validationState(currentRows, false);
          scheduleChangedTracking(container, buildDpoConfigurationPayload(container, currentRows, {
            ...state,
            priceCalculated: false,
          }), {
            source: 'dpo_specialmaal_guard',
            trigger: 'input',
          });
          setBlockedState(container, true, true);
        });
        row.input.addEventListener('blur', () => {
          row.input.setAttribute(TOUCHED_ATTR, 'true');
          setPriceCalculated(container, false, 'Ret mål og beregn prisen igen.');
          const currentRows = fieldsFor(container, rules);
          const state = validationState(currentRows, true);
          scheduleChangedTracking(container, buildDpoConfigurationPayload(container, currentRows, {
            ...state,
            priceCalculated: false,
          }), {
            source: 'dpo_specialmaal_guard',
            trigger: 'blur',
          });
          setBlockedState(container, true, true);
        });
      }
    }

    const form = container.closest('form');
    if (form && !form.hasAttribute('data-naturrum-dpo-submit-guard')) {
      form.setAttribute('data-naturrum-dpo-submit-guard', 'true');
      form.addEventListener('formdata', (event) => {
        const currentRows = fieldsFor(container, rules);
        shapeDpoFormData(event.formData, dimensionValuesFromRows(currentRows));
      });
      form.addEventListener('submit', (event) => {
        const currentRows = fieldsFor(container, rules);
        const state = validationState(currentRows, true);
        const priceCalculated = isPriceCalculated(container);
        setBlockedState(container, !state.valid || !priceCalculated, true);
        if (!state.valid || !priceCalculated) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (state.valid && !priceCalculated) {
            setPriceCalculationMessage(container, 'Beregn pris før du lægger produktet i kurven.');
            priceCalculationButton(container)?.focus();
          } else {
            state.firstInvalid?.focus();
          }
          return;
        }
        syncDpoPropertyInputs(form, dimensionValuesFromRows(currentRows));
        const configuration = container._naturrumDpoSpecialmaalCalculatedConfiguration
          || buildDpoConfigurationPayload(container, currentRows, {
            ...state,
            priceCalculated: true,
          });
        syncQuoteTrackingPropertyInputs(form, container, configuration, null);
        trackConfiguratorEvent(container, 'naturrum_configured_add_to_cart', configuration, {
          source: 'dpo_specialmaal_form',
          trigger: 'submit',
          price_shown_event_id: container.getAttribute(LAST_PRICE_EVENT_ID_ATTR) || null,
        });
      }, true);
    }

    if (form) {
      for (const button of addButtons(container)) {
        if (button.hasAttribute('data-naturrum-dpo-property-shaping')) continue;
        button.setAttribute('data-naturrum-dpo-property-shaping', 'true');
        button.addEventListener('click', () => {
          const currentRows = fieldsFor(container, rules);
          const state = validationState(currentRows, false);
          if (state.valid && isPriceCalculated(container)) {
            syncDpoPropertyInputs(form, dimensionValuesFromRows(currentRows));
            const configuration = container._naturrumDpoSpecialmaalCalculatedConfiguration
              || buildDpoConfigurationPayload(container, currentRows, {
                ...state,
                priceCalculated: true,
              });
            syncQuoteTrackingPropertyInputs(form, container, configuration, null);
          } else if (state.valid) {
            setPriceCalculationMessage(container, 'Beregn pris før du lægger produktet i kurven.');
            setBlockedState(container, true, true);
          }
        }, true);
      }
    }

    const state = validationState(rows, false);
    const mustCalculatePrice = !isPriceCalculated(container);
    setBlockedState(container, !state.valid || mustCalculatePrice, true);
    return true;
  }

  function boot() {
    if (typeof document === 'undefined' || !document.querySelector) return;

    let applying = false;
    const guardedApply = () => {
      if (applying) return;
      applying = true;
      window.requestAnimationFrame?.(() => {
        applyGuard(document);
        applying = false;
      }) || setTimeout(() => {
        applyGuard(document);
        applying = false;
      }, 0);
    };

    guardedApply();
    document.addEventListener('DOMContentLoaded', guardedApply);
    new MutationObserver(guardedApply).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  window.NaturrumDpoSpecialmaalGuard = {
    applyGuard,
    buildDpoConfigurationPayload,
    dimensionKeyFromLabel,
    parseDkkPriceText,
    rangeHelpText,
    shapeDpoFormData,
    shapeDpoPropertiesObject,
    shouldSuppressPriceForResults,
    trackPriceAggregate,
    validateDimensionValue,
  };

  boot();
})();
